import { useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  jumpToTaskSectionWhenReady,
  readTaskItem,
  readTaskOpportunityId,
  readTaskPreferStoryboard,
  readTaskSection,
  TASK_SECTION_SEARCH_PARAM,
} from "@/features/task-progress/lib/task-section";

/**
 * Section-only jumps (e.g. "N scripts" with no scriptId) — scrolls the marked
 * region / `[data-task-scroller]`, then drops `?section=` so a later click re-runs.
 *
 * Precise targets have a single owner elsewhere and clear `section` themselves:
 * - `?variant=` → ScriptStoryboardView
 * - `?scriptId=` / `?highlight=` / `?opportunityId=` without variant → ScriptsTab
 */
export function useTaskSectionJump(): void {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const section = readTaskSection(search);
  const item = readTaskItem(search);
  const opportunityId = readTaskOpportunityId(search);
  const preferStoryboard = readTaskPreferStoryboard(search);

  useEffect(() => {
    if (!section) return;

    // Precise scroll owned elsewhere — leave `section` so that owner can key
    // off its reappearance (repeat card clicks).
    if (preferStoryboard || item || opportunityId) return;

    const jump = jumpToTaskSectionWhenReady(
      section,
      () => {
        void navigate({
          to: ".",
          search: (prev) => ({ ...prev, [TASK_SECTION_SEARCH_PARAM]: undefined }),
          replace: true,
        });
      },
      Date.now,
    );
    return jump.cancel;
  }, [section, item, opportunityId, preferStoryboard, navigate]);
}
